import React, { useCallback, useState } from 'react';
import { connect } from 'react-redux';
import { PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js';
import cn from 'classnames';
import { bindActionCreators } from 'redux';

import beSetupStripeSubscription from 'actions/billing/beSetupStripeSubscription.action';
import Button from 'components/form/Button.component';
import CONF from 'config/conf.singleton';
import { useTranslation } from 'providers/GILT.provider';
import { useTheme } from 'providers/Theme.provider';
import getAnyOrVoid from 'utils/accessors/getAnyOrVoid.util';

import CN from './SetupForm.module.scss';

// NOTE: Can't be read in react dev tools, check in future
const SetupForm = ({
  beSetupStripeSubscription,
  handleGoBack,
  hideLoader,
  isNew,
  salonId,
  setShowModal,
  showLoader,
  subscriptionId,
}) => {
  const stripe = useStripe();
  const elements = useElements();
  const { isDarkMode } = useTheme();
  const { t } = useTranslation();

  const [errorMessage, setErrorMessage] = useState(null);

  const handleSubmit = useCallback(async () => {
    if (!stripe || !elements) {
      // Stripe.js has not yet loaded.
      // Make sure to disable form submission until Stripe.js has loaded.
      return;
    }
    const response = await stripe.confirmSetup({
      // `Elements` instance that was used to create the Payment Element
      elements,
      confirmParams: {
        return_url: `${CONF.salonUrl}${salonId}/settings/billing`,
      },
      redirect: 'if_required',
    });

    if (response.error) {
      // This point will only be reached if there is an immediate error when
      // confirming the payment. Show error to your customer (for example, payment
      // details incomplete)
      const errorMessage = getAnyOrVoid('error.message', response);
      setErrorMessage(errorMessage);
      return;
    }

    if (isNew || subscriptionId === null) {
      const id = getAnyOrVoid('setupIntent.id', response);

      showLoader();

      const result = await beSetupStripeSubscription({ salonId, intentId: id });
      if (result.is.ok) {
        hideLoader();
        setShowModal(true);
      } else {
        setErrorMessage(t('staffAndBusiness.subAndBilling.billingDetails.cardFormErrorMsg'));
        hideLoader();
      }

      return;
    }
    setErrorMessage('');
    setShowModal(true);

    // Your customer will be redirected to your `return_url`. For some payment
    // methods like iDEAL, your customer will be redirected to an intermediate
    // site first to authorize the payment, then redirected to the `return_url`.
  }, [
    beSetupStripeSubscription,
    elements,

    hideLoader,
    isNew,
    salonId,
    setShowModal,
    showLoader,
    stripe,
    subscriptionId,
    t,
  ]);

  return (
    <form
      className={cn({
        [CN.component]: true,
        [CN.dark]: isDarkMode,
      })}
    >
      <PaymentElement />
      <div className={CN.buttonWrap}>
        <Button variant="outlineForce" onClick={handleGoBack}>
          {t('common.buttons.cancelUpp')}
        </Button>
        <Button variant="primaryForce" onClick={handleSubmit} disabled={!stripe || !elements}>
          {t('common.buttons.saveChanges')}
        </Button>
      </div>
      {/* Show error message to your customers */}
      {errorMessage && <div className={CN.error}>{errorMessage} </div>}
    </form>
  );
};

const mapDispatchToProps = (dispatch) =>
  bindActionCreators(
    {
      beSetupStripeSubscription,
    },
    dispatch,
  );

export default connect(null, mapDispatchToProps)(SetupForm);
